import { message } from "ant-design-vue";
import CodeService from "@/src/api/CodeService.js";
import { timeAgo } from "@/src/utils/utils.js";
import { resolvePRDiffRange } from "@/src/utils/codebaseUtils.js";

const state = {
  // "{repoId}:{prNumber}" — used to detect when the active PR changes
  prKey: null,
  commits: [],
  comments: [],
  prActivities: [],
  loading: false,
  loaded: false,
  mergeability: null,
  loadingMergeability: false,
  updatingBranch: false,
  updateBranchStrategy: "merge",
};

const getters = {
  commits: (state) => state.commits,
  comments: (state) => state.comments,
  prActivities: (state) => state.prActivities,
  loading: (state) => state.loading,
  loaded: (state) => state.loaded,
  mergeability: (state) => state.mergeability,
  loadingMergeability: (state) => state.loadingMergeability,
  updatingBranch: (state) => state.updatingBranch,
  updateBranchStrategy: (state) => state.updateBranchStrategy,

  // Merged, chronologically sorted timeline consumed by PRActivityTab
  timeline: (state) => {
    const commitItems = state.commits.map((c) => ({
      id: c.sha,
      type: "commit",
      author: c.author || {
        name: c.author_name || "Unknown",
        profile_pic: null,
      },
      message: c.message,
      sha: (c.sha || "").slice(0, 7),
      status: c.verified ? "Verified" : null,
      created_at: c.author_date || c.created_at || c.committedDate || null,
    }));

    const commentItems = state.comments.map((c) => ({
      id: c.id,
      type: "comment",
      author: c.author || { name: "Unknown", profile_pic: null },
      text: c.text || c.body || "",
      created_at: c.created_at || null,
    }));

    const activityItems = state.prActivities.map((a) => ({
      id: a._id || a.id,
      type: a.event_type,
      author: a.author || { name: a.author_id || "Unknown", profile_pic: null },
      text: a.comment_body || "",
      reviewState: a.review_state || null,
      participantId: a.participant_id || null,
      participantRole: a.participant_role || null,
      created_at: a.timestamp || null,
    }));

    return [...commitItems, ...commentItems, ...activityItems].sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return ta - tb;
    });
  },
};

const mutations = {
  SET_PR_KEY(state, key) {
    state.prKey = key;
  },
  SET_COMMITS(state, commits) {
    state.commits = commits;
  },
  SET_COMMENTS(state, comments) {
    state.comments = comments;
  },
  SET_PR_ACTIVITIES(state, activities) {
    state.prActivities = activities;
  },
  SET_LOADING(state, val) {
    state.loading = val;
  },
  SET_LOADED(state, val) {
    state.loaded = val;
  },
  ADD_COMMENT(state, comment) {
    state.comments = [...state.comments, comment];
  },
  SET_MERGEABILITY(state, data) {
    state.mergeability = data;
  },
  SET_LOADING_MERGEABILITY(state, val) {
    state.loadingMergeability = val;
  },
  SET_UPDATING_BRANCH(state, val) {
    state.updatingBranch = val;
  },
  SET_UPDATE_BRANCH_STRATEGY(state, val) {
    state.updateBranchStrategy = val;
  },
  RESET(state) {
    state.prKey = null;
    state.commits = [];
    state.comments = [];
    state.prActivities = [];
    state.loading = false;
    state.loaded = false;
    state.mergeability = null;
    state.loadingMergeability = false;
    state.updatingBranch = false;
    state.updateBranchStrategy = "merge";
  },
};

const actions = {
  async fetchPRData({ commit, state, rootGetters, dispatch }, { repoId, pr }) {
    if (!repoId || !pr?.number) return;

    const key = `${repoId}:${pr.number}`;

    // Already cached for this PR — skip re-fetch
    if (state.loaded && state.prKey === key) return;

    commit("SET_PR_KEY", key);
    commit("SET_COMMITS", []);
    commit("SET_COMMENTS", []);
    commit("SET_PR_ACTIVITIES", []);
    commit("SET_LOADING", true);
    commit("SET_LOADED", false);

    try {
      // Ensure users are loaded so author_id resolution works correctly
      if (!rootGetters["users/users"]?.length) {
        try {
          await dispatch("users/fetchUsers", [], { root: true });
        } catch {
          // non-fatal — continue with empty user list
        }
      }

      const resolveUserByEmail = rootGetters["users/resolveCommitAuthorByEmail"];
      const resolveUser = rootGetters["users/resolveCommitAuthor"];
      const { base, head } = resolvePRDiffRange(pr);
      const [commitsResult, commentsResult, activitiesResult] =
        await Promise.allSettled([
          // Commits exclusive to this PR (on head but not yet on base)
          base && head
            ? CodeService.base.commits.list(repoId, { base, head })
            : Promise.resolve({ data: [] }),
          // General (non-inline) comments for the Comments tab
          CodeService.base.pulls.listComments(repoId, pr.number),
          // PR lifecycle events from SuperSales (created, merged, closed, commented, reviewed)
          CodeService.base.pulls.listActivities(pr.id),
        ]);

      // Commits — transport returns res.data directly → shape is { data: [...] }
      if (
        commitsResult.status === "fulfilled" &&
        commitsResult.value?.data?.length
      ) {
        commit(
          "SET_COMMITS",
          commitsResult.value.data.map((c) => {
            const authorEmail = c.committer_email || c.author_email || null;
            const resolved = authorEmail ? resolveUserByEmail(authorEmail) : null;
            // Only use resolved.name if it's an actual user name, not the fallback email
            const authorName =
              resolved && resolved.name !== authorEmail
                ? resolved.name
                : (c.author_name || c.committer_name || c.author?.name || "Unknown");
            const author = {
              id: resolved?.id || null,
              name: authorName,
              profile_pic: resolved?.profile_pic || c.author?.profile_pic || c.author?.avatar_url || null,
            };
            return {
              ...c,
              sha: c.oid || c.sha,
              committedAgo: timeAgo(c.author_date || c.created_at),
              author: author,
            };
          }),
        );
      } else {
        commit("SET_COMMITS", []);
      }

      // Comments — normalize API shape { body, author_id } → { text, author: { name } }
      if (
        commentsResult.status === "fulfilled" &&
        commentsResult.value?.data?.length
      ) {
        commit(
          "SET_COMMENTS",
          commentsResult.value.data
            .filter((c) => !c.comment_type || c.comment_type === "general")
            .map((c) => ({
              ...c,
              text: c.body || c.text || "",
              author: resolveUser(c.author_id),
            })),
        );
      } else {
        commit("SET_COMMENTS", []);
      }

      // PR activities from SuperSales
      if (
        activitiesResult.status === "fulfilled" &&
        activitiesResult.value?.activities?.length
      ) {
        commit(
          "SET_PR_ACTIVITIES",
          activitiesResult.value.activities.map((a) => ({
            ...a,
            author: resolveUser(a.author_id),
            author_id: a.author_id,
          })),
        );
      } else {
        commit("SET_PR_ACTIVITIES", []);
      }

      commit("SET_LOADED", true);
    } catch (error) {
      console.error("Error fetching PR data:", error);
      commit("SET_COMMITS", []);
      commit("SET_COMMENTS", []);
      commit("SET_PR_ACTIVITIES", []);
      commit("SET_LOADED", true);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  addComment({ commit }, comment) {
    commit("ADD_COMMENT", comment);
  },

  async fetchMergeability({ commit }, { repoId, number }) {
    if (!repoId || !number) return;
    commit("SET_LOADING_MERGEABILITY", true);
    try {
      const response = await CodeService.base.pulls.mergeability(repoId, number);
      commit("SET_MERGEABILITY", response?.data ?? response);
    } catch (error) {
      throw error;
    } finally {
      commit("SET_LOADING_MERGEABILITY", false);
    }
  },

  async updateBranch({ commit, state, dispatch }, { repoId, number }) {
    commit("SET_UPDATING_BRANCH", true);
    try {
      const response = await CodeService.base.pulls.updateBranch(
        repoId,
        number,
        state.updateBranchStrategy,
      );
      if (response?.success) {
        message.success("Branch updated successfully");
        await dispatch("fetchMergeability", { repoId, number });
      } else {
        message.error(response?.data?.error || "Failed to update branch");
      }
    } catch (error) {
      console.error("An error occurred while updating branch: ", error);
    } finally {
      commit("SET_UPDATING_BRANCH", false);
    }
  },

  setUpdateBranchStrategy({ commit }, strategy) {
    commit("SET_UPDATE_BRANCH_STRATEGY", strategy);
  },

  resetPRData({ commit }) {
    commit("RESET");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
